import { useState, useMemo } from 'react';

/**
 * Hook personalizado para manejar la selección de año
 * Diseñado para ser reutilizable y fácil de usar por agentes autónomos
 *
 * @param {number} defaultYear - Año por defecto (opcional, usa año actual si no se proporciona)
 * @param {number} yearsBack - Cantidad de años hacia atrás (default: 5)
 * @param {number} yearsForward - Cantidad de años hacia adelante (default: 1)
 * @returns {Object} { selectedYear, setSelectedYear, availableYears, getYearOptions }
 */
const useYearSelector = (defaultYear = null, yearsBack = 5, yearsForward = 1) => {
    const currentYear = new Date().getFullYear();
    const initialYear = defaultYear || currentYear;

    const [selectedYear, setSelectedYear] = useState(initialYear);

    // Generar array de años disponibles
    const availableYears = useMemo(() => {
        const years = [];
        const startYear = currentYear - yearsBack;
        const endYear = currentYear + yearsForward;

        for (let year = endYear; year >= startYear; year--) {
            years.push(year);
        }

        return years;
    }, [currentYear, yearsBack, yearsForward]);

    // Obtener opciones formateadas para select
    const getYearOptions = useMemo(() => {
        return availableYears.map(year => ({
            value: year,
            label: year.toString(),
            isCurrentYear: year === currentYear
        }));
    }, [availableYears, currentYear]);

    return {
        selectedYear,
        setSelectedYear,
        availableYears,
        yearOptions: getYearOptions,
        currentYear
    };
};

export default useYearSelector;
